import { List, QuillEditor } from "@pathwright/blocks/src/shared"
import IconButton from "@pathwright/ui/src/components/button/IconButton"
import Checkbox from "@pathwright/ui/src/components/form/form-checkbox/Checkbox"
import RadioButton from "@pathwright/ui/src/components/form/form-radio/RadioButton"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { TOOLBAR_CONFIG_TYPES } from "@pathwright/ui/src/components/quill/toolbar/QuillToolbar"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import sortBy from "lodash/sortBy"
import { memo, useCallback, useMemo } from "react"
import isEqual from "react-fast-compare"
import styled from "styled-components"
import "../../Cards/sortable-styles.css"
import { QuestionOptionCardStyles } from "../ui"
import {
  generateOption,
  getQuestionOptions,
  isMultipleChoice,
  normalizeSingleCheckboxCorrectAnswer,
  normalizeMCOrdering,
  toggleCorrectAnswer
} from "../utils"

const OptionEditRow = styled.div`
  ${QuestionOptionCardStyles()}

  // make space for placeholder
  .Checkbox > span:last-of-type,
  .RadioButton > span:last-of-type {
    width: 100%;
  }

  .ql-tooltip {
    width: auto !important;
  }

  .Pathicon {
    display: block;
    font-style: normal;
  }

  ${(p) =>
    p.disabled
      ? `
    transform: none !important;
  `
      : ``}

  ${(p) =>
    p.blockStyles &&
    `
    .IconButton.EditButton i.Pathicon {
      color: ${p.blockStyles.styleAttributes.color} !important;
    }
  `}

  .ContentText .BlockText.quill-editor-noneditable {
    font-size: 18px !important;
    line-height: 1.3em;
    color: var(--styled-block-color) !important;
  }
`

const OptionControls = styled.div`
  display: flex;
  height: 26px;
  align-items: center;
  margin-left: auto;
`

const SortableOptionEditor = memo(
  ({
    option,
    isNew,
    onChange,
    DragHandle,
    placeholder,
    onRemove,
    onToggleCorrectAnswer,
    onClick,
    isMC,
    blockStyles,
    canRemove
  }) => {
    const OptionComponent = isMC ? RadioButton : Checkbox
    const showControls = !isNew

    return (
      <OptionEditRow
        onClick={onClick}
        blockStyles={blockStyles}
        disabled={isNew}
      >
        <OptionComponent disabled={true}>
          <QuillEditor
            key={`option-edit-${option.order}`}
            toolbar={TOOLBAR_CONFIG_TYPES.SIMPLE}
            placeholder={placeholder || "Add an option"}
            html={option.label}
            onChange={onChange}
            controlled
          />
        </OptionComponent>
        {showControls ? (
          <OptionControls>
            <Tooltip title="Mark as the correct answer">
              <IconButton
                icon="check"
                onClick={onToggleCorrectAnswer}
                color={option.isCorrectAnswer === true ? "#008000" : "#ddd"}
              />
            </Tooltip>
            <DragHandle>
              {() => (
                <div>
                  <Pathicon
                    icon="arrow-double-vertical-2"
                    style={{ opacity: "0.7", cursor: "pointer" }}
                    className="EditButton"
                  />
                </div>
              )}
            </DragHandle>
            {canRemove ? (
              <IconButton
                icon="trash"
                onClick={onRemove}
                className="EditButton"
              />
            ) : null}
          </OptionControls>
        ) : null}
      </OptionEditRow>
    )
  },
  (prevProps, nextProps) => {
    if (!isEqual(prevProps, nextProps)) {
      if (process.env.NODE_ENV !== "production") {
        // console.log(`Mc item ${nextProps.index} changed: `, diff(prevProps, nextProps))
      }

      return false
    }

    return true
  }
)

const MultipleChoiceQuestionEditor = (props) => {
  const { data, layout, onChange, blockStyles, type } = props

  const isMC = useMemo(() => isMultipleChoice(layout, data), [layout])
  const minimumOptions = isMC ? 2 : 1

  const options = useMemo(() => {
    let nextOptions = getQuestionOptions(data, isMC)
    nextOptions = normalizeMCOrdering(nextOptions)
    return nextOptions
  }, [data.options, isMC])

  const handleChange = useCallback(
    ({ items }) => {
      onChange({ options: normalizeSingleCheckboxCorrectAnswer(items, isMC) })
    },
    [isMC, onChange]
  )

  const renderItem = useCallback(
    ({ item, index, DragHandle, onRemove, onUpdate, isAtMin }) => {
      const handleUpdate = (html) => {
        onUpdate({ attribute: "label", value: html })
      }

      const handleToggleCorrectAnswer = () => {
        const updateFunction = ({ items, index }) => {
          const updatedOptions = toggleCorrectAnswer(index, items, isMC)
          return { items: updatedOptions }
        }
        onUpdate({ updateFunction })
      }

      return (
        <SortableOptionEditor
          onChange={handleUpdate}
          onRemove={options.length > minimumOptions ? onRemove : undefined}
          onToggleCorrectAnswer={handleToggleCorrectAnswer}
          index={index}
          isNew={!item.label || item.label === ""}
          option={item}
          isMC={isMC}
          type={type}
          DragHandle={DragHandle}
          blockStyles={blockStyles}
          canRemove={options.length > minimumOptions && !isAtMin}
        />
      )
    },
    [isMC, minimumOptions, options.length, type, toggleCorrectAnswer]
  )

  const checkIsAutoGenerated = useCallback(
    (item) => !item.label || item.label === "",
    []
  )

  const generateItem = useCallback(
    ({ order }) => generateOption(order),
    [generateOption]
  )

  return (
    <div style={{ width: "100%" }}>
      <List
        direction="vertical"
        items={sortBy(options, "order")}
        // Keep List's minimum at 1 so edit controls still show when Multiple Choice
        // has exactly two real options plus the auto-generated blank row.
        min={1}
        renderItem={renderItem}
        generateItem={generateItem}
        onChange={handleChange}
        autoGenerateItem
        checkIsAutoGenerated={checkIsAutoGenerated}
        id={props.id}
      />
    </div>
  )
}

export default MultipleChoiceQuestionEditor
